import React, { useEffect, useRef, useState } from 'react';
import { Grid } from '@mui/material';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import AppLayout from '../../SideBar';
import HeadingComponent from '../../../components/ui/HeadingComponent/index.js';
import strings from '../../../constant/strings';
import { LoadingContainer, NoDetailsContainer } from '../../../assets/styles/CommonStyles';
import { buildBuyerIdentity, formatMoney, isShopConfigured } from './utils';
import { buildClient, cartOptions, loadBuyButtonScript, makeBuyerIdentityApplier } from './buyButton';
import {
  ComparePrice, HeaderRow, MutedText, OutlineButton, PageContainer, PriceText,
  ProductBody, ProductCardBox, ProductGrid, ProductImageBox, ProductMeta, ProductTitle,
} from './styles';

/**
 * The shop. Products are read from Shopify with its own SDK and listed here so
 * each one can link to its own page; the cart and the checkout stay Shopify's,
 * rendered by the Buy Button cart widget that travels with the patient.
 */
const ShopProductsContent = () => {
  const history = useHistory();
  const uiRef = useRef(null);
  const identifiedCarts = useRef(new Set());
  const [products, setProducts] = useState([]);
  const [status, setStatus] = useState(isShopConfigured() ? 'loading' : 'unavailable');
  const { userData } = useSelector((state) => ({ userData: state.auth.userData }));
  const buyerIdentity = buildBuyerIdentity(userData);
  const buyerKey = JSON.stringify(buyerIdentity);

  useEffect(() => {
    if (!isShopConfigured()) return undefined;
    let cancelled = false;
    const applyBuyerIdentity = makeBuyerIdentityApplier(buyerIdentity, identifiedCarts.current);

    loadBuyButtonScript()
      .then(() => {
        if (cancelled) return null;
        const client = buildClient();
        return client.product.fetchAll(50).then((list) => {
          if (cancelled) return;
          if (!list || list.length === 0) {
            setStatus('empty');
            return;
          }
          setProducts(list);
          setStatus('ready');
          // The cart widget is created with no products of its own so the
          // floating cart and its contents survive moving between pages.
          const ui = window.ShopifyBuy.UI.init(client);
          uiRef.current = ui;
          ui.createComponent('cart', { options: cartOptions(applyBuyerIdentity) });
        });
      })
      .catch(() => { if (!cancelled) setStatus('error'); });

    return () => {
      cancelled = true;
      if (uiRef.current && typeof uiRef.current.destroyComponent === 'function') {
        ['cart', 'toggle'].forEach((name) => {
          try { uiRef.current.destroyComponent(name); } catch (e) { /* embed already gone */ }
        });
      }
      uiRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyerKey]);

  const openProduct = (product) => history.push(`/patient/shop/${product.handle}`);

  return (
    <PageContainer>
      <HeaderRow>
        <div>
          <HeadingComponent text={{ primary: strings.shop, secondary: strings.shopHeadingText }} />
        </div>
        <OutlineButton onClick={() => history.push('/patient/orders')}>{strings.myOrders}</OutlineButton>
      </HeaderRow>

      {status === 'loading' ? <LoadingContainer>{strings.loading}</LoadingContainer> : null}
      {status === 'empty' ? <NoDetailsContainer><MutedText>{strings.noProductsAvailable}</MutedText></NoDetailsContainer> : null}
      {status === 'error' || status === 'unavailable' ? (
        <NoDetailsContainer><MutedText>{strings.shopUnavailable}</MutedText></NoDetailsContainer>
      ) : null}

      {status === 'ready' ? (
        <ProductGrid style={{ marginTop: 16 }}>
          {products.map((product) => {
            const variant = (product.variants || [])[0] || {};
            const image = (product.images || [])[0] || {};
            const compareAt = variant.compareAtPrice && variant.compareAtPrice.amount;
            const price = variant.price || {};
            const discounted = compareAt && Number(compareAt) > Number(price.amount);
            const percentOff = discounted
              ? Math.round(((Number(compareAt) - Number(price.amount)) / Number(compareAt)) * 100)
              : 0;
            return (
              // The whole card opens the product, the way every shop works.
              <ProductCardBox key={product.id} elevation={0} onClick={() => openProduct(product)}>
                <ProductImageBox>
                  {image.src
                    ? <img src={image.src} alt={image.altText || product.title} />
                    : <ShoppingBagOutlinedIcon sx={{ color: '#cfcfd7', fontSize: 48 }} />}
                </ProductImageBox>
                <ProductBody>
                  <ProductTitle>{product.title}</ProductTitle>
                  <PriceText>
                    {formatMoney(price.amount, price.currencyCode)}
                    {discounted ? (
                      <>
                        <ComparePrice>{formatMoney(compareAt, price.currencyCode)}</ComparePrice>
                        <ComparePrice style={{ color: '#2AAB6C', textDecoration: 'none', fontWeight: 600 }}>
                          {percentOff}% {strings.off}
                        </ComparePrice>
                      </>
                    ) : null}
                  </PriceText>
                  <ProductMeta>
                    {variant.available ? strings.deliveryEstimateNote : strings.outOfStock}
                  </ProductMeta>
                </ProductBody>
              </ProductCardBox>
            );
          })}
        </ProductGrid>
      ) : null}
    </PageContainer>
  );
};

const ShopProducts = () => (
  <Grid container spacing={0}>
    <AppLayout>
      <ShopProductsContent />
    </AppLayout>
  </Grid>
);

export default ShopProducts;
